import React, { useState, useEffect, useRef } from "react";
import OptimizedImg from "../../components/OptimizedImg";
import { cssImageSetValue } from "../../utils/media";

/* ============================================================
   PROJECT 2 — REDESIGNED, matched to Project 1's build quality
   Product: Hospital Equipment Management

   Color system swapped to the SAGVEEK TECHNOLOGIES brand palette:
   Off-white page background · Near-black Deep Purple for CTAs &
   headings · Magenta/Purple accent (matching the logo's
   purple→magenta wordmark gradient) for labels, checkmarks &
   highlights · Soft lavender panel for alternating sections.

   Palette : Off-white #FAF8FC · Lavender Panel #F5EEFA ·
             Deep Purple #3D1259 · Near-black Purple #12061F ·
             Magenta Accent #C0179E · Slate #5A4E68 · Pink #D946A8
   Type    : IBM Plex Sans (display+body) + IBM Plex Mono (readouts)
   Signature: live ECG/waveform motif, monitor-style dashboard card,
              light clinical surface instead of a dark theme.

   NOTE: matches Project 1's integration pattern — rendered directly
   via slug-based state (no react-router-dom dependency), plain <a>
   tags for CTAs. Swap hrefs for real routes once routing exists.

   UPDATE: pricing section removed. Replaced with a lightweight
   "Request a Demo / Talk to Us" CTA panel in the same slot.

   UPDATE 2: real screenshots wired in from public/images/project2/
   - hero image now uses 1.png
   - "Product Screens" gallery section added using 2.png, 3.png, 4.png

   UPDATE 3: the "Live Tracking" section's coded ASSET MONITOR mockup
   panel replaced with the real dashboard screenshot md.png, kept
   inside the same browser-chrome-style frame for consistency.

   UPDATE 4: recolored to match the Sagveek Technologies logo
   (black → deep purple → magenta gradient). Stats strip rebuilt as
   individual hoverable cards with a slower, staggered count-up.

   UPDATE 5: "All Features" section fixed — was wrapped in a
   1fr/1fr grid with only one child (dead space on the right,
   content squeezed left). Now spans full section width as a
   centered label/heading + 4-column tile grid, matching every
   other section's layout pattern.

   UPDATE 6: consolidated the two separate "feature" sections.
   Previously there was a card-style "Features" grid (icon + title +
   desc, using DATA.features) directly above a plain chip-list
   "All Features" section (title-only, using DATA.detailedFeatures)
   — same concept, two different visual languages back to back.
   The short "Features" card grid was removed, and the "All Features"
   list has been restyled into the same hoverable card treatment
   (icon chip, title, lift-on-hover, glow shadow) used everywhere
   else on the page, so there is now a single, consistent feature
   section.

   FIX: restored several <a> tags whose opening `<a` had been
   dropped (only the href/style/onMouse* attributes remained),
   which broke JSX parsing ("Expected corresponding JSX closing
   tag for <div>"). Affected CTAs: "Request a Demo", "Talk to Us",
   "See it live", "Book a Free Demo", "Call Us Now".

   UPDATE 7: added a shared Request-a-Demo mailto system (matching
   the Samone product page pattern). Both "Request a Demo →" (hero)
   and "Book a Free Demo →" (final CTA) now open a pre-filled email
   via DEMO_MAILTO instead of routing to /contact, so subject/body
   stay in sync if either is ever edited. "Talk to Us" (/contact)
   and "Call Us Now" (tel:) are left unchanged since they aren't
   demo-request CTAs.
   ============================================================ */

/* ─── Request-a-Demo mailto link (this product) ───
   Built once and reused by both "Request a Demo" style CTAs so the
   subject/body stay in sync if either is ever edited. */
const DEMO_EMAIL = "hr@sagveektechnologies.in";
const DEMO_SUBJECT = "Demo Request - Hospital Equipment Management (HEM)";
const DEMO_BODY = [
  "Hi Sagveek Technologies team,",
  "",
  "I would like to request a demo of Hospital Equipment Management (HEM), the asset tracking and management platform.",
  "",
  "Please let me know a suitable time to connect.",
  "",
  "Thanks,",
].join("\n");

const DEMO_MAILTO = `mailto:${DEMO_EMAIL}?subject=${encodeURIComponent(
  DEMO_SUBJECT
)}&body=${encodeURIComponent(DEMO_BODY)}`;

const DATA = {
  name: "Medical Equipment Management",
  ticketNo: "HEM-0002",
  category: "Healthcare Management",
  tagline: "Every asset, tracked like a vital sign.",
  description:
    "A robust platform for healthcare facilities to track medical equipment across departments — maintenance schedules, calibration records, usage logs and compliance documentation, all in real time.",
  heroImage: "/images/project2/0.png",
  dashboardImage: "/images/project2/md.png",
  screenshots: [
    { src: "/images/project2/2.png", label: "Asset Dashboard" },
    { src: "/images/project2/3.png", label: "Maintenance Scheduling" },
    { src: "/images/project2/4.png", label: "Compliance Reports" },
  ],
  stats: [
    { value: 300, suffix: "+", label: "Facilities" },
    { value: 97, suffix: "%", label: "Satisfaction" },
    { value: 25, suffix: "%", label: "Downtime Cut" },
    { value: 24, suffix: "/7", label: "Support Desk" },
  ],
  benefits: [
    { icon: "❤", title: "Reduce Equipment Downtime", desc: "Stay ahead of maintenance with automated schedules and proactive alerts." },
    { icon: "◈", title: "Full Asset Visibility", desc: "See the live status and location of every device across every department." },
    { icon: "▣", title: "Centralize Compliance", desc: "Keep calibration certificates and audit documentation organized in one place." },
    { icon: "◷", title: "Track Usage Live", desc: "Monitor usage and lifecycle from purchase intake through retirement." },
  ],
  features: [
    { icon: "🩺", title: "Asset & Inventory Management", desc: "Track every piece of equipment across departments." },
    { icon: "🛠️", title: "Maintenance Scheduling", desc: "Automate preventive maintenance and service reminders." },
    { icon: "📐", title: "Calibration Records", desc: "Maintain accurate, audit-ready calibration history." },
    { icon: "📁", title: "Compliance Documentation", desc: "Store certificates and compliance paperwork centrally." },
    { icon: "📈", title: "Usage Logs", desc: "Record equipment usage by department, shift and staff." },
    { icon: "📊", title: "Real-time Reports", desc: "Custom reports and analytics dashboards, always current." },
  ],
  steps: [
    { n: "01", title: "Register Asset", desc: "Add new equipment with specifications, department and warranty." },
    { n: "02", title: "Schedule Maintenance", desc: "Plan preventive maintenance and calibration timelines." },
    { n: "03", title: "Track Usage", desc: "Monitor usage and location in real time, ward by ward." },
    { n: "04", title: "Compliance Check", desc: "Verify calibration and compliance status ahead of audits." },
    { n: "05", title: "Report & Review", desc: "Generate reports and review equipment performance." },
  ],
  detailedFeatures: [
    "User Roles & Permissions", "Multi-department Management", "Mobile Responsive",
    "Maintenance Scheduling", "Service History", "Compliance Alerts",
    "Calibration Tracking", "Email & SMS Notifications",
    "Warranty Tracking",
  ],
  capabilities: [
    { k: "Deployment", v: "Cloud-based (SaaS)" },
    { k: "Access", v: "Web & Mobile" },
    { k: "Integrations", v: "Vendor Systems, Email, SMS, APIs" },
    { k: "Security", v: "Encrypted & Compliant" },
    { k: "Support", v: "24/7 Email, Chat & Phone" },
    { k: "Scale", v: "Single Clinic → Multi-facility" },
  ],
  testimonials: [
    { text: "We finally have full visibility into every piece of equipment across the hospital.", name: "Dr. Sunita Rao", role: "City Care Hospital", tag: "1,200+ assets" },
    { text: "Calibration tracking used to be a nightmare during audits. Not anymore.", name: "Prakash Shah", role: "Wellness Multispecialty", tag: "Est. 2009" },
    { text: "Maintenance alerts alone saved us from several costly breakdowns.", name: "Neha Joshi", role: "LifeLine Hospital", tag: "4 departments" },
  ],
  faq: [
    { q: "What is Hospital Equipment Management?", a: "A platform that helps healthcare facilities track medical equipment, maintenance, calibration, usage and compliance documentation from a single dashboard." },
    { q: "Do you provide training and support?", a: "Yes — onboarding, training resources and 24/7 support via email, chat and phone." },
    { q: "Can it be used across multiple departments or facilities?", a: "Yes, multi-department and multi-facility management with centralized visibility across all locations." },
    { q: "Is my data secure?", a: "Yes, SSL encrypted, regularly backed up, with role-based access control." },
  ],
};

/* ─── Tokens (Sagveek brand: off-white / deep-purple / magenta system) ─── */
const C = {
  bg: "#FAF8FC",
  surface: "#FFFFFF",
  raised: "#F5EEFA",
  border: "#E6DCEF",
  muted: "#D3B9E3",
  dimText: "#8A7B96",
  subText: "#5A4E68",
  text: "#2A2033",
  bright: "#170B2E",
  teal: "#3D1259",
  tealD: "#12061F",
  tealGlow: "#3D125914",
  coral: "#6C0A9F",
  amber: "#D946A8",
};

const FF = {
  display: "'IBM Plex Sans', system-ui, sans-serif",
  body: "'IBM Plex Sans', system-ui, sans-serif",
  mono: "'IBM Plex Mono', monospace",
};

/* ─── Hooks ─── */
function useInView(threshold = 0.25) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function useCountUp(target, active, duration = 2400, delay = 0) {
  const [n, setN] = useState(0);
  const done = useRef(false);
  useEffect(() => {
    if (!active || done.current) return;
    done.current = true;
    let raf;
    const begin = () => {
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min((now - t0) / duration, 1);
        const ease = 1 - Math.pow(1 - p, 3);
        setN(Math.round(ease * target));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };
    const timer = setTimeout(begin, delay);
    return () => {
      clearTimeout(timer);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [active, target, duration, delay]);
  return n;
}

/* ─── Sub-components ─── */
function StatTile({ value, suffix, label, active, delay = 0 }) {
  const n = useCountUp(value, active, 2400, delay);
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        textAlign: "center",
        padding: "36px 20px",
        background: C.surface,
        border: `1px solid ${hov ? C.coral : C.border}`,
        borderRadius: 16,
        boxShadow: hov ? `0 20px 44px ${C.tealGlow}` : "0 1px 2px rgba(23,11,46,0.05)",
        transform: hov ? "translateY(-5px)" : "none",
        transition: "all 0.25s ease",
      }}
    >
      <div style={{ fontFamily: FF.mono, fontSize: "clamp(36px, 5vw, 50px)", fontWeight: 600, color: C.bright, lineHeight: 1, letterSpacing: "-1px" }}>
        {n}
        <span style={{ color: C.coral }}>{suffix}</span>
      </div>
      <div style={{ fontFamily: FF.body, fontSize: 12.5, color: C.dimText, marginTop: 10, letterSpacing: "0.5px", textTransform: "uppercase" }}>{label}</div>
    </div>
  );
}

function FeatureCard({ icon, title, desc }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? C.raised : C.surface,
        border: `1px solid ${hov ? C.muted : C.border}`,
        borderRadius: 14,
        padding: "26px 24px",
        transition: "all 0.2s ease",
        cursor: "default",
        transform: hov ? "translateY(-3px)" : "none",
        boxShadow: hov ? `0 16px 36px ${C.tealGlow}` : "0 1px 2px rgba(23,11,46,0.04)",
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: hov ? C.teal : C.raised,
          color: hov ? "#fff" : C.teal,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 18,
          marginBottom: 16,
          transition: "all 0.2s ease",
        }}
      >
        {icon}
      </div>
      <h3 style={{ fontFamily: FF.display, fontSize: 16.5, fontWeight: 600, color: C.bright, margin: "0 0 8px", letterSpacing: "-0.2px" }}>{title}</h3>
      <p style={{ fontFamily: FF.body, fontSize: 13.5, color: C.subText, margin: 0, lineHeight: 1.7 }}>{desc}</p>
    </div>
  );
}

/* Compact card for title-only feature entries (used in the consolidated
   "All Features" grid) — same visual language as FeatureCard (icon chip,
   lift + glow on hover) but sized for a single line of text with no
   description, so a 9-item, 4-column grid doesn't feel oversized. */
function FeatureTile({ title }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        background: hov ? C.raised : C.surface,
        border: `1px solid ${hov ? C.muted : C.border}`,
        borderRadius: 12,
        padding: "16px 18px",
        transition: "all 0.2s ease",
        cursor: "default",
        transform: hov ? "translateY(-3px)" : "none",
        boxShadow: hov ? `0 14px 32px ${C.tealGlow}` : "0 1px 2px rgba(23,11,46,0.04)",
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 9,
          background: hov ? C.teal : C.raised,
          color: hov ? "#fff" : C.teal,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          flexShrink: 0,
          transition: "all 0.2s ease",
        }}
      >
        ✓
      </div>
      <span style={{ fontFamily: FF.body, fontSize: 13.5, fontWeight: 600, color: hov ? C.bright : C.text, transition: "color 0.2s ease" }}>{title}</span>
    </div>
  );
}

function Section({ children, style }) {
  return <section style={{ maxWidth: 1120, margin: "0 auto", padding: "88px 40px", ...style }}>{children}</section>;
}

function Label({ text }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        fontFamily: FF.mono,
        fontSize: 11,
        letterSpacing: "2px",
        color: C.coral,
        textTransform: "uppercase",
        marginBottom: 16,
      }}
    >
      <div style={{ width: 16, height: 1.5, background: C.coral }} />
      {text}
      <div style={{ width: 16, height: 1.5, background: C.coral }} />
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <h2
      style={{
        fontFamily: FF.display,
        fontSize: "clamp(28px, 3.6vw, 42px)",
        fontWeight: 700,
        color: C.bright,
        margin: "0 0 16px",
        letterSpacing: "-1px",
        lineHeight: 1.15,
      }}
    >
      {children}
    </h2>
  );
}

/* Animated ECG waveform divider — this product's signature motif */
function EcgDivider() {
  return (
    <div style={{ height: 46, overflow: "hidden", position: "relative" }}>
      <svg
        viewBox="0 0 1200 46"
        preserveAspectRatio="none"
        style={{ width: "200%", height: "100%", animation: "hem-scroll 7s linear infinite" }}
      >
        <polyline
          fill="none"
          stroke={C.coral}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          points="0,23 110,23 130,6 150,40 170,23 380,23 400,23 420,6 440,40 460,23 680,23 700,23 720,6 740,40 760,23 980,23 1000,23 1020,6 1040,40 1060,23 1200,23"
        />
      </svg>
    </div>
  );
}

/* Screenshot card used in the "Product Screens" gallery */
function ScreenshotCard({ src, label }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: hov ? `0 20px 44px ${C.tealGlow}` : "0 1px 2px rgba(23,11,46,0.04)",
        transform: hov ? "translateY(-4px)" : "none",
        transition: "all 0.2s ease",
      }}
    >
      <div style={{ background: C.raised, height: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <OptimizedImg src={src} alt={label} loading="lazy" pictureStyle={{ width: "100%", height: "100%", display: "block" }} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
      </div>
      <div style={{ padding: "14px 18px", borderTop: `1px solid ${C.border}` }}>
        <span style={{ fontFamily: FF.body, fontSize: 13.5, fontWeight: 600, color: C.bright }}>{label}</span>
      </div>
    </div>
  );
}

/* ─── Page ─── */
const ProjectTwoProductDetailPage = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const { ref: statsRef, visible: statsVisible } = useInView();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        .hem-root, .hem-root *{ box-sizing:border-box; }
        @keyframes hem-scroll{ from{ transform:translateX(0); } to{ transform:translateX(-50%); } }
        @media(prefers-reduced-motion:reduce){ .hem-root svg[style]{ animation:none!important; } }
      `}</style>

      <div className="hem-root" style={{ background: C.bg, color: C.text, fontFamily: FF.body, minHeight: "100vh", overflowX: "hidden" }}>
        {/* ══ HERO ══ */}
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            backgroundImage: cssImageSetValue(DATA.heroImage),
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* dark gradient overlay for text contrast against the photo */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(100deg, ${C.tealD}F2 0%, ${C.tealD}D9 32%, ${C.tealD}88 58%, ${C.tealD}33 100%)`,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "-10%",
              right: "-6%",
              width: 520,
              height: 520,
              background: `radial-gradient(circle, ${C.coral}22 0%, transparent 70%)`,
              pointerEvents: "none",
            }}
          />

          <Section style={{ paddingTop: 36, paddingBottom: 29, paddingLeft: 12, position: "relative", zIndex: 1 }}>
            <div style={{ maxWidth: 640 }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  border: `1px solid #ffffff33`,
                  borderRadius: 100,
                  padding: "6px 16px 6px 8px",
                  marginBottom: 28,
                  background: "#ffffff14",
                  backdropFilter: "blur(6px)",
                }}
              >
                <div style={{ background: C.coral, borderRadius: 100, padding: "2px 10px", fontFamily: FF.mono, fontSize: 10, color: "#fff", fontWeight: 600, letterSpacing: "1px" }}>
                  {DATA.category.toUpperCase()}
                </div>
                <span style={{ fontFamily: FF.body, fontSize: 13, color: "#EAF0EE" }}>{DATA.name}</span>
              </div>

              <h1
              style={{
                fontFamily: FF.display,
                fontWeight: 700,
                fontSize: "clamp(36px, 5.4vw, 58px)",
                lineHeight: 1.08,
                letterSpacing: "-1.5px",
                color: "#fff",
                margin: "0 0 18px",
              }}
            >
              Every asset,
              <br />
              tracked like a{" "}
              <span
                style={{
                  color: C.coral,
                  WebkitTextStroke: "0.6px rgba(255,255,255,0.85)",
                  textShadow: "0 1px 12px rgba(0,0,0,0.45)",
                }}
              >
                vital sign.
              </span>
            </h1>

              <p style={{ fontFamily: FF.body, fontSize: "clamp(15px, 1.6vw, 17px)", color: "#D7DEE9", maxWidth: 480, margin: "0 0 36px", lineHeight: 1.75 }}>
                {DATA.description}
              </p>

              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 40 }}>
                <a
                  href={DEMO_MAILTO}
                  style={{
                    fontFamily: FF.body,
                    fontWeight: 600,
                    fontSize: 14.5,
                    padding: "15px 30px",
                    background: C.coral,
                    color: "#fff",
                    textDecoration: "none",
                    borderRadius: 10,
                    transition: "all 0.2s",
                    display: "inline-block",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#9E1281")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = C.coral)}
                >
                  Request a Demo →
                </a>
                <a
                  href="/contact"
                  style={{
                    fontFamily: FF.body,
                    fontWeight: 600,
                    fontSize: 14.5,
                    padding: "15px 30px",
                    border: `1.5px solid #ffffff55`,
                    color: "#fff",
                    textDecoration: "none",
                    borderRadius: 10,
                    transition: "all 0.2s",
                    display: "inline-block",
                    background: "transparent",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#ffffff14";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  Talk to Us
                </a>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" }}>
                {["HIPAA-aware workflows", "Setup in days", "24/7 support"].map((t) => (
                  <div key={t} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.coral }} />
                    <span style={{ fontFamily: FF.body, fontSize: 12.5, color: "#C7D0DC" }}>{t}</span>
                  </div>
                ))}
              </div>

              {/* vitals chip, now inline instead of anchored to an image card */}
              <div
                style={{
                  marginTop: 44,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 12,
                  background: "#ffffff14",
                  border: `1px solid #ffffff33`,
                  backdropFilter: "blur(6px)",
                  borderRadius: 14,
                  padding: "14px 18px",
                }}
              >
                <div style={{ width: 34, height: 34, borderRadius: "50%", background: `${C.coral}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>❤</div>
                <div>
                  <div style={{ fontFamily: FF.mono, fontSize: 16, fontWeight: 600, color: "#fff" }}>99.9%</div>
                  <div style={{ fontFamily: FF.body, fontSize: 10.5, color: "#C7D0DC", letterSpacing: "0.5px", textTransform: "uppercase" }}>Uptime</div>
                </div>
              </div>
            </div>
          </Section>

          <div style={{ padding: "0 40px", position: "relative", zIndex: 1 }}>
            <EcgDivider />
          </div>
        </div>

       

        {/* ══ BENEFITS ══ */}
        <Section>
          <div style={{ marginBottom: 52 }}>
            <div style={{ textAlign: "center" }}>
              <Label text="Key Benefits" />
            </div>
            <SectionHeading>Built to keep every asset healthy.</SectionHeading>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
            {DATA.benefits.map((b, i) => (
              <FeatureCard key={i} {...b} />
            ))}
          </div>
        </Section>

        {/* ══ LIVE TRACKING DASHBOARD SCREENSHOT + PRODUCT SCREENS ══ */}
        <div style={{ borderTop: `1px solid ${C.border}`, background: C.raised }}>
          <Section>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "60px 80px", alignItems: "center", marginBottom: 72 }}>
              <div style={{ position: "relative" }}>
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, overflow: "hidden", boxShadow: `0 30px 70px ${C.tealGlow}` }}>
                  <div style={{ padding: "13px 18px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8, background: C.tealD }}>
                    {["#6C0A9F", "#8B2FC9", "#3D1259"].map((c) => (
                      <div key={c} style={{ width: 9, height: 9, borderRadius: "50%", background: c }} />
                    ))}
                    <span style={{ fontFamily: FF.mono, fontSize: 10.5, color: "#F5EEFAAA", marginLeft: 8, letterSpacing: "1px" }}>ASSET MONITORING DASHBOARD</span>
                  </div>
                  <div style={{ background: C.raised }}>
                    <OptimizedImg
                      src={DATA.dashboardImage}
                      alt="HEM live asset dashboard"
                      loading="lazy"
                      pictureStyle={{ width: "100%", display: "block" }}
                      style={{ width: "100%", height: "auto", display: "block", objectFit: "contain" }}
                    />
                  </div>
                </div>
              </div>
              <div>
                <Label text="Live Tracking" />
                <SectionHeading>Every device, one dashboard, zero blind spots.</SectionHeading>
                <p style={{ fontSize: 15.5, color: C.subText, lineHeight: 1.8, margin: 0 }}>
                  Equipment goes missing between wards. Calibration dates slip past. Audits become a scramble for paperwork. HEM brings every
                  device into one live view, so your team always knows what needs attention next.
                </p>
              </div>
            </div>

            {/* ══ PRODUCT SCREENS GALLERY ══ */}
            <div>
              <div style={{ marginBottom: 40 }}>
                <div style={{ textAlign: "center" }}>
                  <Label text="Product Screens" />
                </div>
                <SectionHeading>
                 Asset monitoring, screen by screen.
                </SectionHeading>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
                {DATA.screenshots.map((s, i) => (
                  <ScreenshotCard key={i} {...s} />
                ))}
              </div>
            </div>
          </Section>
        </div>

        {/* ══ HOW IT WORKS ══ */}
        <div style={{ borderTop: `1px solid ${C.border}` }}>
        <Section>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "60px 80px", alignItems: "start" }}>
            <div>
              <div style={{ textAlign: "center" }}>
                <Label text="How It Works" />
              </div>
              <SectionHeading>
                Five steps from intake
                <br />
                to audit-ready.
              </SectionHeading>
              <p style={{ fontSize: 15, color: C.subText, lineHeight: 1.8, margin: "0 0 40px" }}>
                HEM is built around how real equipment moves through a facility — from registration through maintenance, usage and compliance
                review.
              </p>
              <a
                href="https://kaizenmeds.in/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  fontFamily: FF.body,
                  fontWeight: 600,
                  fontSize: 14,
                  padding: "13px 26px",
                  border: `1.5px solid ${C.teal}`,
                  color: C.teal,
                  textDecoration: "none",
                  borderRadius: 10,
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = C.teal;
                  e.currentTarget.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = C.teal;
                }}
              >
                See it live →
              </a>
            </div>
            <div>
              {DATA.steps.map((s, i) => (
                <div key={i} style={{ display: "flex", gap: 20, paddingBottom: i < DATA.steps.length - 1 ? 28 : 0, position: "relative" }}>
                  {i < DATA.steps.length - 1 && (
                    <div
                      style={{
                        position: "absolute",
                        left: 17,
                        top: 36,
                        bottom: 0,
                        width: 1,
                        background: `linear-gradient(to bottom, ${C.teal}55, transparent)`,
                      }}
                    />
                  )}
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      background: C.raised,
                      border: `1.5px solid ${C.teal}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      zIndex: 1,
                    }}
                  >
                    <span style={{ fontFamily: FF.mono, fontSize: 10, fontWeight: 600, color: C.teal }}>{s.n}</span>
                  </div>
                  <div style={{ paddingTop: 6 }}>
                    <h4 style={{ fontFamily: FF.display, fontSize: 16.5, fontWeight: 600, color: C.bright, margin: "0 0 5px" }}>{s.title}</h4>
                    <p style={{ fontFamily: FF.body, fontSize: 13.5, color: C.subText, margin: 0, lineHeight: 1.7 }}>{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </div>

        {/* ══ FEATURES (consolidated) ══ */}
        <div style={{ borderTop: `1px solid ${C.border}`, background: C.raised }}>
          <Section style={{ width: "100%" }}>
            <div style={{ marginBottom: 40 }}>
              <div style={{ textAlign: "center" }}>
                <Label text="Features" />
              </div>
              <SectionHeading>
                {DATA.detailedFeatures.length} modules. One platform. No feature paywalls.
              </SectionHeading>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, width: "100%" }}>
              {DATA.detailedFeatures.map((t, i) => (
                <FeatureTile key={i} title={t} />
              ))}
            </div>
          </Section>
        </div>

        {/* ══ TESTIMONIALS ══ */}
        <div style={{ borderTop: `1px solid ${C.border}` }}>
          <Section>
            <div style={{ marginBottom: 52 }}>
              <div style={{ textAlign: "center" }}>
                <Label text="From the Ward" />
              </div>
              <SectionHeading>Facilities that made the switch.</SectionHeading>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
              {DATA.testimonials.map((t, i) => (
                <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "30px 26px", position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", top: -14, right: 18, fontFamily: FF.display, fontSize: 90, color: `${C.teal}0E`, lineHeight: 1, pointerEvents: "none", userSelect: "none" }}>❝</div>
                  <div style={{ display: "flex", gap: 2, marginBottom: 18 }}>
                    {[...Array(5)].map((_, j) => (
                      <span key={j} style={{ color: C.amber, fontSize: 13 }}>★</span>
                    ))}
                  </div>
                  <p style={{ fontFamily: FF.body, fontSize: 14.5, color: C.text, lineHeight: 1.75, margin: "0 0 26px", position: "relative" }}>"{t.text}"</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${C.teal}33, ${C.coral}33)`,
                        border: `1px solid ${C.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: FF.display,
                        fontSize: 13,
                        fontWeight: 700,
                        color: C.teal,
                      }}
                    >
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <div style={{ fontFamily: FF.body, fontWeight: 600, fontSize: 13.5, color: C.bright }}>{t.name}</div>
                      <div style={{ fontFamily: FF.mono, fontSize: 10, color: C.dimText, letterSpacing: "0.5px", marginTop: 2 }}>
                        {t.role} · {t.tag}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </div>

        

        {/* ══ FAQ ══ */}
        <div style={{ borderTop: `1px solid ${C.border}` }}>
          <Section>
            <div style={{ maxWidth: 720, margin: "0 auto" }}>
              <div style={{ marginBottom: 48 }}>
                <div style={{ textAlign: "center" }}>
                  <Label text="FAQ" />
                </div>
                <SectionHeading>Common questions.</SectionHeading>
              </div>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", background: C.surface }}>
                {DATA.faq.map((f, i) => {
                  const open = openFaq === i;
                  return (
                    <div key={i} style={{ borderBottom: i < DATA.faq.length - 1 ? `1px solid ${C.border}` : "none" }}>
                      <div
                        onClick={() => setOpenFaq(open ? -1 : i)}
                        style={{
                          padding: "20px 24px",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 16,
                          background: open ? C.raised : "transparent",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => {
                          if (!open) e.currentTarget.style.background = C.raised;
                        }}
                        onMouseLeave={(e) => {
                          if (!open) e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <span style={{ fontFamily: FF.body, fontWeight: 600, fontSize: 15, color: open ? C.bright : C.text }}>{f.q}</span>
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            border: `1px solid ${open ? C.teal : C.muted}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: open ? C.teal : C.dimText,
                            fontSize: 14,
                            flexShrink: 0,
                            transition: "all 0.2s",
                            transform: open ? "rotate(45deg)" : "none",
                          }}
                        >
                          +
                        </div>
                      </div>
                      {open && (
                        <div style={{ padding: "0 24px 20px", fontFamily: FF.body, fontSize: 14, color: C.subText, lineHeight: 1.8, borderTop: `1px solid ${C.border}` }}>
                          <div style={{ paddingTop: 16 }}>{f.a}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </Section>
        </div>

        {/* ══ FINAL CTA ══ */}
        <div style={{ borderTop: `1px solid ${C.border}`, position: "relative", overflow: "hidden", background: C.tealD }}>
          <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse at center, ${C.teal}55 0%, transparent 70%)` }} />
          <div style={{ position: "absolute", top: 20, left: 0, right: 0, opacity: 0.5 }}>
            <svg viewBox="0 0 1200 46" preserveAspectRatio="none" style={{ width: "200%", height: 46, animation: "hem-scroll 7s linear infinite" }}>
              <polyline
                fill="none"
                stroke={C.coral}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                points="0,23 110,23 130,6 150,40 170,23 380,23 400,23 420,6 440,40 460,23 680,23 700,23 720,6 740,40 760,23 980,23 1000,23 1020,6 1040,40 1060,23 1200,23"
              />
            </svg>
          </div>
          <Section style={{ position: "relative", zIndex: 2, textAlign: "center" }}>
            <Label text="Get Started" />
            <h2
              style={{
                fontFamily: FF.display,
                fontSize: "clamp(32px, 4.6vw, 54px)",
                fontWeight: 700,
                color: "#fff",
                margin: "0 0 20px",
                letterSpacing: "-1.5px",
                lineHeight: 1.1,
              }}
            >
              Ready to put every asset
              <br />
              <span style={{ color: C.coral }}>on the record?</span>
            </h2>
            <p style={{ fontFamily: FF.body, fontSize: 16, color: "#D7DEE9", maxWidth: 480, margin: "0 auto 44px", lineHeight: 1.7 }}>
              Join {DATA.stats[0].value}+ facilities already running on HEM. Get a free personalised demo — no credit card, no commitment.
            </p>
            <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
              <a
                href={DEMO_MAILTO}
                style={{
                  fontFamily: FF.body,
                  fontWeight: 600,
                  fontSize: 15.5,
                  padding: "16px 34px",
                  background: C.coral,
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: 10,
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#9E1281";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = C.coral;
                  e.currentTarget.style.transform = "none";
                }}
              >
                Book a Free Demo →
              </a>
              <a
                href="tel:+91"
                style={{
                  fontFamily: FF.body,
                  fontWeight: 500,
                  fontSize: 15.5,
                  padding: "16px 34px",
                  border: `1px solid #ffffff44`,
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: 10,
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#ffffff88")}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#ffffff44")}
              >
                📞 Call Us Now
              </a>
            </div>
          </Section>
        </div>
      </div>
    </>
  );
};

export default ProjectTwoProductDetailPage;